import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import api from "../lib/api";
import { OuterNav } from "../layouts/PublicNav";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend, CartesianGrid } from "recharts";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import ChartTypeToggle from "../components/ChartTypeToggle";
import SeoPageHead from "../components/SeoPageHead";
import OodelXFooter from "../components/OodelXFooter";
import { useLanguage } from "../hooks/useLanguage";
import { Lock } from "lucide-react";

const COLORS = ["#023B24", "#16A34A", "#0055FF", "#F4C430", "#8B5CF6", "#14B8A6"];

// Returns the URL prefix to use for sibling links — keeps panelists/admins
// inside their portal layout instead of falling out to public PublicNav pages.
function usePathPrefix(section) {
  const loc = useLocation();
  if (loc.pathname.startsWith(`/panelist/${section}`)) return `/panelist/${section}`;
  return `/${section}`;
}

// ITER 90/110 — a PANELIST-gated tracker (t.locked === true) renders inline,
// in its natural grid position, as an opaque "unlock" card — never its real
// name/description/data. `lock_reason` picks the right message + CTA:
// anonymous visitors get "sign up"; signed-in patrons with an incomplete
// profile get "complete your profile" (same gate as taking a survey).
// Subscriber/enterprise-gated trackers never reach the frontend at all
// (filtered out server-side, not counted).
function LockedTrackerCard({ tracker }) {
  const needsProfile = tracker.lock_reason === "profile_incomplete";
  return (
    <div
      className="relative bg-white border border-neutral-200 rounded-2xl p-8 overflow-hidden shadow-sm"
      data-testid={`tracker-locked-${tracker.id}`}
    >
      <div className="select-none pointer-events-none blur-sm opacity-40" aria-hidden="true">
        <div className="h-3 w-1/3 bg-neutral-200 rounded mb-3" />
        <div className="h-6 w-2/3 bg-neutral-200 rounded mb-2" />
        <div className="h-3 w-full bg-neutral-100 rounded" />
      </div>
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-white/85 backdrop-blur-sm text-center px-6">
        <Lock size={20} className="text-mango" />
        {tracker.sector && <div className="overline text-[10px]">{String(tracker.sector).toUpperCase()}</div>}
        <div className="font-bold tracking-tight text-neutral-900">{needsProfile ? "Complete your profile to unlock" : "Panelist-only tracker"}</div>
        <Link
          to={needsProfile ? "/panelist/profile" : `/register?next=${encodeURIComponent("/trackers")}`}
          className="mt-2 inline-flex items-center gap-1 bg-mango text-white rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-mango/90 transition-colors"
          data-testid={`tracker-locked-cta-${tracker.id}`}
        >
          {needsProfile ? "Complete profile →" : "Upgrade & unlock →"}
        </Link>
      </div>
    </div>
  );
}

export function TrackersList() {
  const [trackers, setTrackers] = useState([]);
  const [loading, setLoading] = useState(true);
  const prefix = usePathPrefix("trackers");
  const { lang } = useLanguage();
  useEffect(() => {
    setLoading(true);
    api.get(`/public/trackers?lang=${lang}`).then((r) => {
      setTrackers(r.data.trackers || []);
    }).finally(() => setLoading(false));
  }, [lang]);

  return (
    <div className="min-h-screen bg-white themable-public">
      <SeoPageHead
        pageKey="trackers"
        path="/trackers"
        defaultTitle="Time-Series Trackers — Live Pakistan Consumer Data | oodel."
        defaultDescription="Track sentiment, brand health and consumer behaviour across Pakistan with weekly-updated time-series charts."
      />
      <OuterNav />
      <div className="max-w-[1400px] mx-auto px-6 py-12">
        <div className="pb-4 mb-10">
          <div className="overline mb-2 text-mango">SECTION — TRACKERS</div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-neutral-900">All time-series trackers</h1>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="trackers-grid">
          {trackers.map((t) => (
            t.locked ? (
              <LockedTrackerCard key={t.id} tracker={t} />
            ) : (
              <Link key={t.id} to={`${prefix}/${t.id}`} data-testid={`tracker-${t.id}`} className="bg-white border border-neutral-200 rounded-2xl shadow-sm hover:shadow-md hover:border-mango/40 hover:-translate-y-0.5 transition-all overflow-hidden flex flex-col">
                {t.image && (
                  <div className="aspect-video bg-neutral-50 overflow-hidden" data-testid={`tracker-image-${t.id}`}>
                    <img src={t.image} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
                  </div>
                )}
                <div className="p-8">
                  <div className="overline mb-2 text-mango">{t.category}</div>
                  <h2 className="text-2xl font-bold tracking-tight mb-2 text-neutral-900">{t.name}</h2>
                  <p className="text-neutral-500 text-sm">{t.description}</p>
                </div>
              </Link>
            )
          ))}
          {!loading && trackers.length === 0 && <div className="bg-white border border-neutral-200 rounded-2xl p-8 text-neutral-500 col-span-full">No trackers yet.</div>}
        </div>
      </div>
    </div>
  );
}

export function TrackerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("overall");
  const [aggBucket, setAggBucket] = useState("raw"); // raw | week | month | quarter | year
  const [smoother, setSmoother] = useState(false);   // 3-wave moving average
  const [highQuality, setHighQuality] = useState(false); // hide rows with n < 30

  useEffect(() => {
    const f = filter === "overall" ? "" : `?filter=${encodeURIComponent(filter)}`;
    const sep = f ? "&" : "?";
    const minN = highQuality ? 30 : 1;
    api.get(`/trackers/${id}/data${f}${sep}min_n=${minN}`).then((r) => setData(r.data));
  }, [id, filter, highQuality]);

  if (!data) return <div className="p-12">Loading…</div>;

  // ---- TIME-AGGREGATION ----
  function bucketKey(dateStr) {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    const Y = d.getUTCFullYear();
    const M = d.getUTCMonth() + 1;
    if (aggBucket === "year") return `${Y}`;
    if (aggBucket === "quarter") return `${Y}-Q${Math.ceil(M / 3)}`;
    if (aggBucket === "month") return `${Y}-${String(M).padStart(2, "0")}`;
    if (aggBucket === "week") {
      // ISO week
      const tmp = new Date(Date.UTC(Y, M - 1, d.getUTCDate()));
      const dayNum = (tmp.getUTCDay() + 6) % 7;
      tmp.setUTCDate(tmp.getUTCDate() - dayNum + 3);
      const firstThu = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 4));
      const wk = 1 + Math.round(((tmp - firstThu) / 86400000 - 3 + ((firstThu.getUTCDay() + 6) % 7)) / 7);
      return `${tmp.getUTCFullYear()}-W${String(wk).padStart(2, "0")}`;
    }
    return dateStr; // raw
  }

  const aggregateSeries = () => {
    if (aggBucket === "raw") return data.series;
    const buckets = {};
    data.series.forEach((row) => {
      const k = bucketKey(row.date);
      const b = buckets[k] || (buckets[k] = { date: k, n_sample: 0, _count: 0, _sum: {} });
      b._count++;
      b.n_sample += row.n_sample || 0;
      data.options.forEach((o) => {
        if (row[o.key] != null) {
          b._sum[o.key] = (b._sum[o.key] || 0) + row[o.key];
        }
      });
    });
    return Object.values(buckets).sort((a, b) => a.date.localeCompare(b.date)).map((b) => {
      const out = { date: b.date, n_sample: b.n_sample };
      data.options.forEach((o) => {
        if (b._sum[o.key] != null) out[o.key] = Math.round((b._sum[o.key] / b._count) * 10) / 10;
      });
      return out;
    });
  };

  const smooth = (series) => {
    if (!smoother || series.length < 3) return series;
    return series.map((row, i) => {
      const window = series.slice(Math.max(0, i - 2), i + 1);
      const out = { date: row.date, n_sample: row.n_sample };
      data.options.forEach((o) => {
        const vals = window.map((r) => r[o.key]).filter((v) => v != null);
        if (vals.length) out[o.key] = Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10;
      });
      return out;
    });
  };

  const series = smooth(aggregateSeries());

  const exportCSV = () => {
    if (!series.length) return;
    const keys = ["date", "n_sample", ...data.options.map((o) => o.key)];
    const csv = [keys.join(","), ...series.map((row) => keys.map((k) => JSON.stringify(row[k] ?? "")).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `tracker-${id}-${aggBucket}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const filterOptions = [
    { value: "overall", label: "Overall" },
    { value: "gender:Male", label: "Gender — Male" },
    { value: "gender:Female", label: "Gender — Female" },
    { value: "age:18-24", label: "Age — 18–24" },
    { value: "age:25-34", label: "Age — 25–34" },
    { value: "age:35-44", label: "Age — 35–44" },
    { value: "age:45-54", label: "Age — 45–54" },
    { value: "age:55+", label: "Age — 55+" },
    { value: "region:Punjab", label: "Region — Punjab" },
    { value: "region:Sindh", label: "Region — Sindh" },
    { value: "region:KPK", label: "Region — KPK" },
    { value: "region:Balochistan", label: "Region — Balochistan" },
    { value: "region:ICT", label: "Region — ICT" },
    { value: "region:GB", label: "Region — GB" },
    { value: "region:AJK", label: "Region — AJK" },
    { value: "sec:A", label: "SEC — A" },
    { value: "sec:B", label: "SEC — B" },
    { value: "sec:C", label: "SEC — C" },
    { value: "sec:D", label: "SEC — D" },
    { value: "sec:E", label: "SEC — E" },
  ];

  const Chip = ({ value, label }) => (
    <button
      type="button"
      onClick={() => setAggBucket(value)}
      data-testid={`agg-${value}`}
      className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${aggBucket === value ? "bg-mango text-white border-mango" : "border-neutral-200 hover:border-mango/50 text-neutral-600"}`}
    >
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-white themable-public">
      <OuterNav />
      <div className="max-w-[1400px] mx-auto px-6 py-12">
        <div className="border-b border-neutral-200 pb-6 mb-8 flex flex-col md:flex-row justify-between items-start gap-6">
          <div>
            <div className="overline mb-2 text-mango">{data.tracker.category}</div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-neutral-900 mb-2" data-testid="tracker-title">{data.tracker.name}</h1>
            <p className="text-neutral-500">{data.tracker.description}</p>
            <div className="mt-3"><ChartTypeToggle kind="tracker" id={data.tracker.id} current={data.tracker.chart_type || "line"} /></div>
          </div>
          <div className="flex flex-col gap-2">
            <div className="overline">CROSSTAB FILTER</div>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger data-testid="tracker-filter" className="w-64 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                {filterOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <button onClick={exportCSV} data-testid="tracker-csv" className="overline text-xs text-mango hover:underline self-end mt-1">↓ Download CSV</button>
          </div>
        </div>

        {/* Aggregation toggle */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="overline mr-2">AGGREGATE BY</span>
          <Chip value="raw" label="Per wave" />
          <Chip value="week" label="Weekly" />
          <Chip value="month" label="Monthly" />
          <Chip value="quarter" label="Quarterly" />
          <Chip value="year" label="Yearly" />
          <label className="ml-4 inline-flex items-center gap-2 text-xs cursor-pointer text-neutral-600">
            <input type="checkbox" checked={smoother} onChange={(e) => setSmoother(e.target.checked)} data-testid="agg-smoother" />
            3-wave moving average
          </label>
          <label className="ml-3 inline-flex items-center gap-2 text-xs cursor-pointer text-neutral-600">
            <input type="checkbox" checked={highQuality} onChange={(e) => setHighQuality(e.target.checked)} data-testid="hq-toggle" />
            Hide low-sample points (n &lt; 30)
          </label>
          <span className="ml-auto text-xs text-neutral-400 font-mono">{series.length} data point{series.length === 1 ? "" : "s"}</span>
        </div>

        <div className="border border-neutral-200 rounded-2xl p-6 bg-white shadow-sm">
          <div className="flex justify-between items-baseline mb-3">
            <div className="overline text-mango">PERCENTAGE OVER TIME</div>
            <div className="font-mono text-xs text-neutral-400">N = {(() => {
              const last = series[series.length - 1];
              return last?.n_sample || "—";
            })()}</div>
          </div>
          <div className="h-[420px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series} margin={{ top: 20, right: 30, left: 0, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
                <XAxis dataKey="date" stroke="#71717a" />
                <YAxis stroke="#71717a" domain={[0, 'dataMax + 10']} unit="%" />
                <Tooltip
                  contentStyle={{ background: '#171717', border: 'none', color: '#fff', fontFamily: 'IBM Plex Mono', borderRadius: 12, padding: '8px 12px' }}
                  formatter={(value, name, props) => [`${value}% (N=${props?.payload?.n_sample ?? '—'})`, name]}
                  labelFormatter={(d) => `${aggBucket === "raw" ? "Wave" : aggBucket} · ${d}`}
                />
                <Legend />
                {data.options.map((o, i) => (
                  <Line key={o.key} type="monotone" name={o.text} dataKey={o.key} stroke={COLORS[i % COLORS.length]} strokeWidth={3} dot={{ r: 4 }} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Latest data table */}
        {series.length > 0 && (
          <div className="mt-10 bg-white border border-neutral-200 rounded-2xl p-6 shadow-sm">
            <div className="overline mb-4 text-mango">LATEST — {series[series.length - 1].date}</div>
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-neutral-200">
                  <th className="text-left py-3 font-mono uppercase text-xs tracking-widest text-neutral-400">Option</th>
                  <th className="text-right py-3 font-mono uppercase text-xs tracking-widest text-neutral-400">Latest %</th>
                </tr>
              </thead>
              <tbody>
                {data.options.map((o) => {
                  const last = series[series.length - 1];
                  return (
                    <tr key={o.key} className="border-b border-neutral-100">
                      <td className="py-3">{o.text}</td>
                      <td className="py-3 text-right font-mono font-semibold">{last[o.key] ?? "—"}{last[o.key] != null ? "%" : ""}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <OodelXFooter />
    </div>
  );
}
